import React, { useState } from 'react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Ticket, QrCode } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { API_URL as API } from '../config/api';

const TicketCard = ({ ticket, index }) => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language === 'fr' ? fr : enUS;
  const [qrOpen, setQrOpen] = useState(false);
  
  const formattedDate = format(new Date(ticket.created_at), 'PPP', { locale });
  const qrUrl = `${API}/tickets/${ticket.ticket_id}/qr`;
  const isTest = ticket.is_test === true;

  // Traductions pour le dialogue QR
  const qrDialogTexts = {
    fr: {
      title: isTest ? '🧪 Billet de DÉMONSTRATION' : 'Votre Billet EDENLIFE',
      description: isTest ? 'Ce billet est un SPECIMEN - Ne participe pas au tirage' : 'Scannez ce code pour vérifier votre billet',
      ticketNumber: 'Numéro de billet',
      qrError: 'Erreur de chargement du QR code',
      price: 'Prix',
      testWarning: '⚠️ BILLET TEST - NON VALIDE POUR LE TIRAGE'
    },
    en: {
      title: isTest ? '🧪 DEMO Ticket' : 'Your EDENLIFE Ticket',
      description: isTest ? 'This ticket is a SPECIMEN - Not valid for the draw' : 'Scan this code to verify your ticket',
      ticketNumber: 'Ticket number',
      qrError: 'Error loading QR code',
      price: 'Price',
      testWarning: '⚠️ TEST TICKET - NOT VALID FOR DRAW'
    },
    ht: {
      title: isTest ? '🧪 Tikè DEMONSTRASYON' : 'Tikè EDENLIFE Ou',
      description: isTest ? 'Tikè sa a se yon ECHANTYON - Li pa patisipe nan tiraj la' : 'Eskane kòd sa a pou verifye tikè ou',
      ticketNumber: 'Nimewo tikè',
      qrError: 'Erè nan chajman QR kòd la',
      price: 'Pri',
      testWarning: '⚠️ TIKÈ TÈS - PA VALAB POU TIRAJ'
    },
    es: {
      title: isTest ? '🧪 Boleto de DEMOSTRACIÓN' : 'Su Boleto EDENLIFE',
      description: isTest ? 'Este boleto es un ESPÉCIMEN - No participa en el sorteo' : 'Escanee este código para verificar su boleto',
      ticketNumber: 'Número de boleto',
      qrError: 'Error al cargar el código QR',
      price: 'Precio',
      testWarning: '⚠️ BOLETO DE PRUEBA - NO VÁLIDO PARA SORTEO'
    },
    pt: {
      title: isTest ? '🧪 Bilhete de DEMONSTRAÇÃO' : 'Seu Bilhete EDENLIFE',
      description: isTest ? 'Este bilhete é um ESPÉCIME - Não participa do sorteio' : 'Escaneie este código para verificar seu bilhete',
      ticketNumber: 'Número do bilhete',
      qrError: 'Erro ao carregar o código QR',
      price: 'Preço',
      testWarning: '⚠️ BILHETE TESTE - NÃO VÁLIDO PARA SORTEIO'
    }
  };

  const qrTexts = qrDialogTexts[i18n.language] || qrDialogTexts.fr;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05 }}
      className={`rounded-xl p-6 border-2 shadow-md hover:shadow-xl transition-all duration-300 ${
        isTest 
          ? 'bg-gradient-to-br from-yellow-100 to-orange-100 border-yellow-400' 
          : 'bg-gradient-to-br from-secondary/20 to-accent/20 border-secondary/30'
      }`}
      data-testid={`ticket-card-${index}`}
    >
      {/* Test Badge */}
      {isTest && (
        <div className="mb-3 -mt-2">
          <span className="inline-flex items-center gap-1 bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            🧪 DÉMONSTRATION - NON VALIDE
          </span>
        </div>
      )}
      
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isTest ? 'bg-yellow-200' : 'bg-secondary/20'
          }`}>
            <Ticket className={`w-6 h-6 ${isTest ? 'text-yellow-600' : 'text-secondary'}`} />
          </div>
          <div>
            <p className="text-sm text-gray-600">{t('tickets.ticketNumber')}</p>
            <p className={`text-2xl font-bold ${isTest ? 'text-yellow-700' : 'text-primary'}`} style={{ fontFamily: 'Playfair Display, serif' }}>
              {ticket.ticket_number}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600">{t('tickets.status')}</p>
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
            isTest 
              ? 'bg-yellow-200 text-yellow-800' 
              : 'bg-accent/20 text-accent'
          }`}>
            {isTest ? '🧪 TEST' : ticket.status}
          </span>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <p>{t('tickets.purchaseDate')}: {formattedDate}</p>
        </div>
        
        <Dialog open={qrOpen} onOpenChange={setQrOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className={`rounded-full ${isTest ? 'border-yellow-500 text-yellow-700 hover:bg-yellow-50' : ''}`}
              data-testid={`view-qr-${index}`}
            >
              <QrCode className="w-4 h-4 mr-2" />
              QR Code
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                {qrTexts.title}
              </DialogTitle>
              <DialogDescription className="text-center">
                {qrTexts.description}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center space-y-4 py-4">
              {isTest && (
                <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg p-3 w-full text-center">
                  <p className="text-yellow-800 font-bold text-sm">{qrTexts.testWarning}</p>
                </div>
              )}
              <div className={`rounded-2xl p-6 text-center ${
                isTest 
                  ? 'bg-gradient-to-br from-yellow-500 to-orange-500' 
                  : 'bg-gradient-to-br from-primary to-accent'
              }`}>
                <p className="text-white text-sm mb-2">{qrTexts.ticketNumber}</p>
                <p className={`text-3xl font-bold ${isTest ? 'text-white' : 'text-secondary'}`}>{ticket.ticket_number}</p>
              </div>
              <div className={`bg-white p-4 rounded-xl border-4 ${isTest ? 'border-yellow-400' : 'border-primary'} relative`}>
                {isTest && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                    <span className="text-yellow-500/40 text-4xl font-bold rotate-[-30deg]">SPECIMEN</span>
                  </div>
                )}
                <img 
                  src={qrUrl} 
                  alt="QR Code" 
                  className="w-64 h-64"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{ display: 'none' }} className="w-64 h-64 flex items-center justify-center text-gray-500">
                  {qrTexts.qrError}
                </div>
              </div>
              <p className="text-sm text-gray-600 text-center">
                {qrTexts.price}: ${ticket.price} {ticket.currency}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </motion.div>
  );
};

export default TicketCard;